import i18n from '@/utils/i18n';
import InfoIcon from '@assets/icons/info.svg';
import React from 'react';
import { BORDER } from '@/styles';
import { Button } from '@/components/button';
import { CachedImage } from '@usepepper/ui/atoms/cached-image';
import { createStyles } from '@/contexts/theme-context';
import { CreditRequestBatchLineItem } from '@/contexts/credit-request-context-types';
import { CreditRequestItemSubtitleV2 } from './credit-request-item-subtitle-v2';
import { formatDateWithYear } from '@/utils/date';
import { getTranslationValue } from '@/utils/translations';
import { OrderableItemListItemImage } from '@/components/orders/orderable-item-list-item-image';
import { QuantitySelector } from '@/components/quantity-selector';
import { Text, View } from 'react-native';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useItemDisplayOptions } from '@/contexts/new-order-context';
import { useVariantPackInfoContext } from '@/contexts/variant-pack-info-context';

interface Props {
  batchLineItem: CreditRequestBatchLineItem;
  maxQuantity?: number;
  onInfoIconPress: () => void;
  removeFromBasket: (uuid: string) => void;
  updateItemQuantity: (uuid: string, quantity: number) => void;
}

const HIT_SLOP = {
  bottom: 8,
  left: 8,
  right: 8,
  top: 8,
};

export const CreditRequestBasketItem: React.FC<Props> = ({
  batchLineItem,
  maxQuantity,
  onInfoIconPress,
  removeFromBasket,
  updateItemQuantity,
}) => {
  const hasGuaranteeInformation = batchLineItem.guaranteed !== undefined;
  const metadata = batchLineItem.item.metadata!;
  const photoURLs = batchLineItem.photoURLs;
  const { currencyCode } = useVariantPackInfoContext();
  const { hidePlaceholderImages } = useItemDisplayOptions();
  const [styles, theme] = useStyles();
  const formattedDate = batchLineItem.fulfillmentDate
    ? formatDateWithYear(batchLineItem.fulfillmentDate)
    : null;

  return (
    <View style={styles.base}>
      <View style={styles.imageDescriptionContainer}>
        <OrderableItemListItemImage
          alt={metadata.displayName}
          hidePlaceholderImages={hidePlaceholderImages}
          imageURLs={metadata.photoURLs ?? []}
          style={BORDER.primary}
        />
        <View style={styles.imageDescription}>
          <Typography numberOfLines={1} variant="heading-sm">
            {metadata.displayName}
          </Typography>

          <Text numberOfLines={1}>
            {hasGuaranteeInformation ? (
              <Typography
                color={
                  batchLineItem.guaranteed
                    ? theme.sk.color.text.success
                    : theme.sk.color.text.warning
                }
                variant="paragraph-sm-600"
              >
                {i18n.t(
                  batchLineItem.guaranteed
                    ? 'credit_request.guaranteed'
                    : 'credit_request.not_guaranteed',
                )}
              </Typography>
            ) : null}
            <Typography
              color={theme.sk.color.text.tertiary}
              variant="paragraph-sm-600"
            >
              {hasGuaranteeInformation ? ' • ' : ''}
              {batchLineItem.fulfillmentDate
                ? i18n.t('fulfilled_date', { date: formattedDate })
                : i18n.t('never_fulfilled')}
            </Typography>
          </Text>
        </View>
        {!batchLineItem.eligibleForCredit ? (
          <Button
            hitSlop={HIT_SLOP}
            Icon={InfoIcon}
            iconProps={{
              height: 16,
              stroke: theme.sk.color.icon.tertiary,
              width: 16,
            }}
            onPress={onInfoIconPress}
            size="xsmall"
            style={styles.iconButton}
            testID="ineligible-item-info-icon"
            trackingData={null}
            type="tertiary"
          />
        ) : null}
      </View>
      <View style={styles.cardContainer}>
        <View style={styles.cardHeader}>
          <CreditRequestItemSubtitleV2
            currencyCode={currencyCode}
            displayString={batchLineItem.selectedPack}
            sku={metadata.externalID}
            unitCount={batchLineItem.quantity}
            unitPriceInMicros={batchLineItem.priceInMicros}
          />
          <QuantitySelector
            canDecrementToZero
            isFocused={false}
            maxQuantity={maxQuantity}
            onChange={(_, quantity) => {
              if (quantity === 0) {
                removeFromBasket(batchLineItem.uuid);
              } else {
                updateItemQuantity(batchLineItem.uuid, quantity);
              }
            }}
            quantity={batchLineItem.quantity}
          />
        </View>
        <View style={styles.cardFooter}>
          <Typography
            color={theme.sk.color.text.primary}
            numberOfLines={2}
            variant="paragraph-md"
          >
            {[
              getTranslationValue(
                batchLineItem?.creditRequestReason?.title || '',
              ),
              batchLineItem.notes,
            ]
              .filter((line) => line !== '')
              .join('. ')}
          </Typography>
          {photoURLs && photoURLs.length > 0 ? (
            <View style={styles.photosWrapper}>
              {photoURLs.map((photo, index) => (
                <CachedImage
                  alt={`Item image #${index}`}
                  contentFit="cover"
                  key={photo}
                  source={{ uri: photo }}
                  style={styles.itemImagePreview}
                  testID={`credit-request-item-image-${index}`}
                />
              ))}
            </View>
          ) : null}
        </View>
      </View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    marginBottom: 24,
  },
  cardContainer: {
    borderColor: theme.colors.stroke,
    borderRadius: 8,
    borderWidth: 1,
  },
  cardFooter: {
    backgroundColor: theme.colors.background,
    borderRadius: 8,
    padding: 16,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  iconButton: {
    paddingHorizontal: 0,
  },
  imageDescription: {
    alignSelf: 'flex-start',
    flex: 1,
  },
  imageDescriptionContainer: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 12,
  },
  itemImagePreview: {
    borderRadius: theme.sk.radii.radiusMedium,
    height: 32,
    width: 32,
  },
  photosWrapper: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 8,
  },
}));
